export type PromotionClaimEvaluation = {
  evidence_no?: number;
  validation_state: string;
  quote_outcome: "APPLIED" | "BLOCKED" | string;
  reason_code?: string | null;
  claim_instance_id?: string | null;
  claim_definition_id?: string | null;
  promotion_version_id?: string | null;
  claim_status?: string | null;
  binding_mode?: string | null;
  expires_at?: string | null;
  credential_hint?: string | null;
  applied_discount_amount?: number | null;
  claim_definition?: {
    code?: string;
    display_name?: string;
    public_slug?: string;
  } | null;
};

export type PromotionClaimEvidence =
  | { credential_code: string }
  | { claim_instance_id: string; customer_id: string };

export type PromotionCustomerClaimSelection = {
  claimInstanceId: string;
  customerId: string;
  promotionVersionId: string;
};

type PromotionClaimSessionState = {
  credentialCode: string | null;
  customerClaim: PromotionCustomerClaimSelection | null;
  evaluation: PromotionClaimEvaluation | null;
  revision: number;
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const CUSTOMER_CLAIM_MARKER_PREFIX = "MBOS_CUSTOMER_CLAIM:";

let state: PromotionClaimSessionState = {
  credentialCode: null,
  customerClaim: null,
  evaluation: null,
  revision: 0,
};

const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

function customerClaimMarker(input: Pick<PromotionCustomerClaimSelection, "claimInstanceId" | "customerId">) {
  return `${CUSTOMER_CLAIM_MARKER_PREFIX}${input.claimInstanceId}:${input.customerId}`;
}

export function readPromotionClaimSession() {
  return state;
}

export function subscribePromotionClaimSession(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setPromotionClaimCredential(value: string) {
  const credentialCode = value.trim().toUpperCase();
  if (!credentialCode) {
    clearPromotionClaimSession();
    return state;
  }
  state = {
    credentialCode,
    customerClaim: null,
    evaluation: null,
    revision: state.revision + 1,
  };
  emit();
  return state;
}

export function setPromotionCustomerClaim(input: PromotionCustomerClaimSelection) {
  if (!UUID_RE.test(input.claimInstanceId) || !UUID_RE.test(input.customerId) || !UUID_RE.test(input.promotionVersionId)) {
    throw new Error("PROMOTION_CLAIM_EVIDENCE_INVALID");
  }
  state = {
    credentialCode: customerClaimMarker(input),
    customerClaim: { ...input },
    evaluation: null,
    revision: state.revision + 1,
  };
  emit();
  return state;
}

export function promotionClaimEvidenceFromCredential(value: string): PromotionClaimEvidence {
  const raw = value.trim();
  if (!raw) throw new Error("PROMOTION_CLAIM_EVIDENCE_INVALID");
  if (raw.startsWith(CUSTOMER_CLAIM_MARKER_PREFIX)) {
    const encoded = raw.slice(CUSTOMER_CLAIM_MARKER_PREFIX.length);
    const [claimInstanceId, customerId, ...rest] = encoded.split(":");
    if (rest.length || !UUID_RE.test(claimInstanceId ?? "") || !UUID_RE.test(customerId ?? "")) {
      throw new Error("PROMOTION_CLAIM_EVIDENCE_INVALID");
    }
    return { claim_instance_id: claimInstanceId, customer_id: customerId };
  }
  const credentialCode = raw.toUpperCase();
  if (credentialCode.length > 128) throw new Error("PROMOTION_CLAIM_EVIDENCE_INVALID");
  return { credential_code: credentialCode };
}

export function normalizePromotionClaimEvidence(
  value: PromotionClaimEvidence[] | undefined,
): PromotionClaimEvidence[] {
  if ((value?.length ?? 0) > 10) throw new Error("PROMOTION_CLAIM_EVIDENCE_INVALID");
  return (value ?? []).map((item) => {
    if ("credential_code" in item) return promotionClaimEvidenceFromCredential(item.credential_code);
    const claimInstanceId = item.claim_instance_id?.trim() ?? "";
    const customerId = item.customer_id?.trim() ?? "";
    if (!UUID_RE.test(claimInstanceId) || !UUID_RE.test(customerId)) {
      throw new Error("PROMOTION_CLAIM_EVIDENCE_INVALID");
    }
    return { claim_instance_id: claimInstanceId, customer_id: customerId };
  });
}

export function promotionClaimEvidenceFromSession(
  session: PromotionClaimSessionState = state,
): PromotionClaimEvidence[] {
  return session.credentialCode ? [promotionClaimEvidenceFromCredential(session.credentialCode)] : [];
}

export function setPromotionClaimEvaluation(revision: number, evaluation: PromotionClaimEvaluation | null) {
  if (revision !== state.revision || !state.credentialCode) return false;
  state = { ...state, evaluation };
  emit();
  return true;
}

export function clearPromotionClaimSession() {
  state = {
    credentialCode: null,
    customerClaim: null,
    evaluation: null,
    revision: state.revision + 1,
  };
  emit();
  return state;
}
